import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { AlertTriangle, ChevronDown, Search } from 'lucide-react';
import { useState } from 'react';
import { EmptyState, PageHeader } from '@/components/page-header';
import { toast } from '@/components/toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { api } from '@/lib/api';
import { ltr } from '@/lib/arabic';
import { useMe } from '@/lib/clients';
import { formatAmount, formatDate } from '@/lib/format';
import { useLanguage, useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import {
  modeLabel,
  templateKindLabel,
  useRecurringTemplates,
  type RecurringTemplate,
} from '@/lib/recurring';
import {
  DOCUMENT_TEMPLATE_KINDS,
  documentKindLabel,
  documentPath,
  isDocumentTemplateKind,
  type DocumentTemplateKind,
} from '@/lib/recurring-documents';
import { scheduleDescription } from '@/lib/recurring-form';

export const Route = createFileRoute('/accounting/recurring/')({ component: RecurringList });

/** Screen A7 (document 06): recurring transaction templates, list pattern. */
function RecurringList() {
  const orgId = useOrgId();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const tr = useT();
  const lang = useLanguage();
  const bidi = (s: string) => (lang === 'ar' ? ltr(s) : s);
  const me = useMe();
  const canEdit = me.data?.isStaff ?? false;
  const templates = useRecurringTemplates();
  const [q, setQ] = useState('');
  const [deleting, setDeleting] = useState<RecurringTemplate | null>(null);

  const pause = useMutation({
    mutationFn: ({ t, paused }: { t: RecurringTemplate; paused: boolean }) =>
      api<RecurringTemplate>(
        `/orgs/${orgId}/recurring-templates/${t.id}/${paused ? 'pause' : 'resume'}`,
        { method: 'POST' },
      ),
    onSuccess: async (t) => {
      await qc.invalidateQueries({ queryKey: ['recurring', orgId] });
      toast(
        t.paused
          ? tr('accountingPages.recurringList.paused', { name: t.name })
          : t.nextDate
            ? tr('accountingPages.recurringList.resumedNext', {
                name: t.name,
                date: bidi(formatDate(t.nextDate)),
              })
            : tr('accountingPages.recurringList.resumed', { name: t.name }),
      );
    },
    onError: (e) => toast(e.message),
  });
  const remove = useMutation({
    mutationFn: (t: RecurringTemplate) =>
      api<void>(`/orgs/${orgId}/recurring-templates/${t.id}`, { method: 'DELETE' }),
    onSuccess: async (_v, t) => {
      await qc.invalidateQueries({ queryKey: ['recurring', orgId] });
      toast(tr('accountingPages.recurringList.deleted', { name: t.name }));
      setDeleting(null);
    },
  });

  const createDraft = useMutation({
    mutationFn: (t: RecurringTemplate) =>
      api<{ document: { id: string; kind: DocumentTemplateKind } }>(
        `/orgs/${orgId}/recurring-templates/${t.id}/use-document`,
        {
          method: 'POST',
          body: JSON.stringify(t.due && t.nextDate ? { occurrenceDate: t.nextDate } : {}),
        },
      ),
    onSuccess: async (r, t) => {
      await qc.invalidateQueries();
      toast(tr(`accountingPages.recurringList.draftCreated.${r.document.kind}`, { name: t.name }));
      void navigate({ to: documentPath(r.document.kind, r.document.id) as never });
    },
    onError: (e) => toast(e.message),
  });
  /** "Could not create the 01/11/2026 entry: …" under a template whose last run failed. */
  const createFailed = (t: RecurringTemplate): string => {
    const vars = {
      date: t.nextDate ? bidi(formatDate(t.nextDate)) : '',
      error: t.lastError ?? '',
    };
    if (t.kind === 'journal_entry' || isDocumentTemplateKind(t.kind))
      return tr(`accountingPages.recurringList.createFailed.${t.kind}`, vars);
    return tr('accountingPages.recurringList.createFailedOther', {
      ...vars,
      kind: lang === 'ar' ? t.kindLabel : t.kindLabel.toLowerCase(),
    });
  };
  // journal entries open the form prefilled; documents are created as drafts to review
  const use = (t: RecurringTemplate) => {
    if (!isDocumentTemplateKind(t.kind))
      return void navigate({ to: '/accounting/journal-entries/new', search: { template: t.id } });
    // one draft per click: a second "Use" while the first is saving would create two documents
    if (!createDraft.isPending) createDraft.mutate(t);
  };
  // after every hook (createDraft above), so the hook order never changes with the client
  if (!orgId)
    return (
      <EmptyState
        title={tr('shell.selectClient')}
        hint={tr('accountingPages.recurringList.selectClientHint')}
      />
    );
  const rows = (templates.data ?? []).filter((t) =>
    q.trim() ? t.name.toLowerCase().includes(q.trim().toLowerCase()) : true,
  );
  const newMenu = (label: string) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="gap-1">
          {label} <ChevronDown className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem asChild>
          <Link to="/accounting/recurring/new">
            {tr('accountingPages.recurringKind.journal_entry')}
          </Link>
        </DropdownMenuItem>
        {DOCUMENT_TEMPLATE_KINDS.map((k) => (
          <DropdownMenuItem key={k.kind} asChild>
            <Link to="/accounting/recurring/new" search={{ kind: k.kind }}>
              {documentKindLabel(k.kind)}
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <div>
      <PageHeader
        title={tr('accountingPages.recurringList.title')}
        description={tr('accountingPages.recurringList.description')}
        actions={canEdit ? newMenu(tr('common.new')) : null}
      />
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Search className="absolute top-2.5 start-2.5 size-4 text-muted-foreground" aria-hidden />
          <Input
            className="ps-8"
            placeholder={tr('accountingPages.recurringList.search')}
            aria-label={tr('accountingPages.recurringList.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>
      {templates.isPending ? (
        <p className="text-sm text-muted-foreground">{tr('common.loading')}</p>
      ) : templates.isError ? (
        <EmptyState
          title={tr('accountingPages.recurringList.couldNotLoad')}
          hint={templates.error.message}
        />
      ) : !templates.data.length ? (
        <EmptyState
          title={tr('accountingPages.recurringList.emptyTitle')}
          hint={tr('accountingPages.recurringList.emptyHint')}
          action={canEdit ? newMenu(tr('accountingPages.recurringList.newTemplate')) : undefined}
        />
      ) : (
        <div className="rounded-lg border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{tr('accountingPages.recurringList.col.templateName')}</TableHead>
                <TableHead>{tr('accountingPages.recurringList.col.type')}</TableHead>
                <TableHead>{tr('accountingPages.recurringList.col.interval')}</TableHead>
                <TableHead>{tr('accountingPages.recurringList.col.previousDate')}</TableHead>
                <TableHead>{tr('accountingPages.recurringList.col.nextDate')}</TableHead>
                <TableHead>{tr('accountingPages.recurringList.col.contact')}</TableHead>
                <TableHead className="text-end">
                  {tr('accountingPages.recurringList.col.amount')}
                </TableHead>
                <TableHead>{tr('accountingPages.recurringList.col.mode')}</TableHead>
                <TableHead className="w-[150px]">
                  {tr('accountingPages.recurringList.col.action')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => (
                <TableRow key={t.id} data-testid={`template-${t.name}`}>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1.5">
                      {canEdit ? (
                        <Link
                          to="/accounting/recurring/$templateId"
                          params={{ templateId: t.id }}
                          className="font-medium hover:text-primary hover:underline"
                        >
                          {t.name}
                        </Link>
                      ) : (
                        <span className="font-medium">{t.name}</span>
                      )}
                      {t.paused ? (
                        <Badge variant="secondary">
                          {tr('accountingPages.recurringList.badgePaused')}
                        </Badge>
                      ) : null}
                      {t.due && t.mode === 'reminder' ? (
                        <Badge variant="outline">
                          {tr('accountingPages.recurringList.badgeDue')}
                        </Badge>
                      ) : null}
                    </div>
                    {t.lastError ? (
                      <p className="mt-1 flex items-start gap-1 text-xs text-destructive">
                        <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
                        <span>{createFailed(t)}</span>
                      </p>
                    ) : null}
                  </TableCell>
                  <TableCell>{templateKindLabel(t.kind, t.kindLabel)}</TableCell>
                  <TableCell>
                    {lang === 'ar' && t.schedule
                      ? scheduleDescription(t.schedule)
                      : (t.interval ?? '—')}
                  </TableCell>
                  <TableCell>{t.previousDate ? formatDate(t.previousDate) : ''}</TableCell>
                  <TableCell>{t.nextDate && !t.paused ? formatDate(t.nextDate) : ''}</TableCell>
                  <TableCell>{t.contactName ?? ''}</TableCell>
                  <TableCell className="tnum text-end">
                    {t.currency} {formatAmount(t.amount)}
                  </TableCell>
                  <TableCell>{modeLabel(t.mode)}</TableCell>
                  <TableCell>
                    {canEdit ? (
                      <div className="flex items-center">
                        {t.due && t.mode === 'reminder' ? (
                          <Button variant="link" size="sm" className="px-1" onClick={() => use(t)}>
                            {tr('accountingPages.recurringList.use')}
                          </Button>
                        ) : (
                          <Button variant="link" size="sm" className="px-1" asChild>
                            <Link
                              to="/accounting/recurring/$templateId"
                              params={{ templateId: t.id }}
                            >
                              {tr('common.edit')}
                            </Link>
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label={tr('accountingPages.recurringList.moreActions', {
                                name: t.name,
                              })}
                            >
                              <ChevronDown className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="z-50">
                            <DropdownMenuItem
                              onSelect={() =>
                                void navigate({
                                  to: '/accounting/recurring/$templateId',
                                  params: { templateId: t.id },
                                })
                              }
                            >
                              {tr('common.edit')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              disabled={createDraft.isPending}
                              onSelect={() => use(t)}
                            >
                              {tr('accountingPages.recurringList.use')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onSelect={() =>
                                void navigate({
                                  to: '/accounting/recurring/new',
                                  search: { copyOf: t.id },
                                })
                              }
                            >
                              {tr('accountingPages.recurringList.duplicate')}
                            </DropdownMenuItem>
                            {t.mode !== 'unscheduled' ? (
                              <DropdownMenuItem
                                onSelect={() => pause.mutate({ t, paused: !t.paused })}
                              >
                                {t.paused
                                  ? tr('accountingPages.recurringList.resume')
                                  : tr('accountingPages.recurringList.pause')}
                              </DropdownMenuItem>
                            ) : null}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem variant="destructive" onSelect={() => setDeleting(t)}>
                              {tr('accountingPages.recurringList.delete')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog
        open={Boolean(deleting)}
        onOpenChange={(o) => (!o ? (setDeleting(null), remove.reset()) : undefined)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {tr('accountingPages.recurringList.deleteTitle', { name: deleting?.name ?? '' })}
            </DialogTitle>
            <DialogDescription>
              {tr('accountingPages.recurringList.deleteDescription')}
            </DialogDescription>
          </DialogHeader>
          {remove.error ? (
            <p role="alert" className="text-sm text-destructive">
              {remove.error.message}
            </p>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              {tr('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              disabled={remove.isPending}
              onClick={() => deleting && remove.mutate(deleting)}
            >
              {remove.isPending
                ? tr('accountingPages.recurringList.deleting')
                : tr('accountingPages.recurringList.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
